import {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {emptyFilter, emptyResult, Filter, Result, Search} from '@types';
import {getQuery, postSearch} from '../search-common';

export const SearchContext = createContext<Search | undefined>(undefined);

export const useSearchContext = (): Search => {
    const context = useContext(SearchContext);

    if (!context) {
        throw new Error('useSearchContext must be used within the SearchContext.Provider');
    }

    return context;
};

export const useSearchContextValue = (): Search => {
    const [filter, setFilter] = useState<Filter>(emptyFilter);
    const [loading, setLoading] = useState<boolean>(false);
    const [result, setResult] = useState<Result>(emptyResult);

    const updateFilter = useCallback((newFilter: Partial<Filter>): void => setFilter({
        ...filter,
        ...newFilter,
        from: !!newFilter.from ? newFilter.from : 0
    }), [filter, setFilter]);

    const search = useCallback(async (): Promise<void> => {
        const {searchString, sources, from, size} = filter;

        if (!!searchString) {
            setLoading(true);
            const query: string = getQuery(searchString, sources, from, size);
            const result = await postSearch(query);
            setResult(result);
            setLoading(false);
        } else {
            setResult(emptyResult);
        }
    }, [filter, setResult, setLoading]);

    const getSearchPath = useCallback(({q, src = []}) => `q=${q}&src=${src.join(',')}`,
        []);

    useEffect(() => {
        search();
    }, [search]);

    return useMemo(() => ({
        filter,
        updateFilter,
        loading,
        result,
        getSearchPath
    }), [
        filter,
        updateFilter,
        loading,
        result,
        getSearchPath
    ]);
};
